import {
  AdjacentType,
  ComparisonType,
  ErrData,
  ErrDataPromise,
  LogicalOperatorType,
  Traversal,
  ValidBusinessObject,
  ValidBusinessObjectList,
} from '@iotv/iotv-v3-types'
import { ValidModelType } from '../classDependentTypes/ValidModelClasses'
import { BranchingQueryDefinition } from '../util/dynamodb/GraphQuery/BranchingQueryDefinition'
import { getNestedStructureBranchingQuery } from './groupHierarchyv2'
import { edgeTypeMatrix } from '../classDependentTypes/EdgeTypes/EdgeTypeMatrix'
import { NodeEdgeMapper } from '../model/constructs/NodeEdgeMapper'
import { getParentEdgeTypeId } from '../classDependentTypes/EdgeTypes/edgeTypeMatrixFns'
import { GetBranchingQueryDefinitionResultsRequest } from './types'

const debug = process.env.REACT_APP_DEBUG && false
const GROUPER_TYPE: ValidModelType & 'Group' = 'Group'
const USER_TYPE: ValidModelType & 'User' = 'User'

export type IndirectGroupedTypeToQueryInput = {
  querySubjects: ValidBusinessObjectList
  /** the type of start objects */
  querySubjectType: string
  /** the type of item contained in the target groups */
  allowedGroupSupportedType: string
}

/** returns a function bound to  querySubjects which will return allowed groups as an array of ValidBusinessObjects  from a nodeEdgeMapper */
export const getIndirectGroupedTypeQuery = ({
  querySubjects,
  querySubjectType,
  allowedGroupSupportedType,
}: IndirectGroupedTypeToQueryInput): ((applicationData: NodeEdgeMapper<ValidBusinessObject>) => ValidBusinessObjectList) => {
  const filteredQuerySubjects = querySubjects.filter((item) => item.type === querySubjectType)

  const SubjectTypeToSubjectTypeGroupType = new BranchingQueryDefinition({
    id: 'SubjectTypeToSubjectTypeGroupType',
    path: [
      {
        adjacencyType: AdjacentType.PARENT,
        objectTypeId: GROUPER_TYPE,
        edgeTypeId: getParentEdgeTypeId(querySubjectType, GROUPER_TYPE),
        nodeFilters: [
          { setOperation: LogicalOperatorType.AND, filters: [{ key: 'supportedType', predicate: ComparisonType.EQUALS, value: querySubjectType }] },
        ],
      },
    ],
    subQueries: [],
  })

  const SubjectTypeToAllSubjectTypeGroups = getNestedStructureBranchingQuery({
    groupObjectType: GROUPER_TYPE,
    containedObjectType: querySubjectType,
    traversal: Traversal.ASCENDING,
    includeSupportedTypeInstances: false,
  })

  const OtherGroupDescendingHierarchy = getNestedStructureBranchingQuery({
    groupObjectType: GROUPER_TYPE,
    containedObjectType: allowedGroupSupportedType,
    traversal: Traversal.DESCENDING,
    includeSupportedTypeInstances: false,
  })

  const allowsEdgeDirection = querySubjectType === USER_TYPE ? AdjacentType.CHILD : AdjacentType.PARENT

  const SubjectTypeGroupsToOtherGroupType = new BranchingQueryDefinition({
    id: 'SubjectTypeGroupsAllowsOtherGroupType',
    path: [
      {
        adjacencyType: allowsEdgeDirection,
        objectTypeId: GROUPER_TYPE,
        edgeTypeId: edgeTypeMatrix.Group.Group.group_allows_group.edgeTypeId,
        nodeFilters: [
          { setOperation: LogicalOperatorType.AND, filters: [{ key: 'supportedType', predicate: ComparisonType.EQUALS, value: allowedGroupSupportedType }] },
        ],
      },
    ],
    subQueries: [],
  })

  const query = (applicationData: NodeEdgeMapper<ValidBusinessObject>): ValidBusinessObjectList => {
    if (!OtherGroupDescendingHierarchy) {
      console.log(`WARN: OtherGroupDescendingHierarchy is not defined `)
    }
    const SubjectTypeGroupsRes = applicationData?.applyBranchingQuery(filteredQuerySubjects, SubjectTypeToSubjectTypeGroupType!)
    const SubjectTypeGroups = SubjectTypeGroupsRes.getNodesOfType(GROUPER_TYPE)
    const ascendantGroupsRes = applicationData?.applyBranchingQuery(SubjectTypeGroups, SubjectTypeToAllSubjectTypeGroups!)
    const ascendantGroups = ascendantGroupsRes.getNodesOfType(GROUPER_TYPE)
    const allowedGroupsRes = applicationData?.applyBranchingQuery(ascendantGroups, SubjectTypeGroupsToOtherGroupType)
    const allowedGroups = allowedGroupsRes.getNodesOfType(GROUPER_TYPE).filter((n) => n.supportedType === allowedGroupSupportedType)
    const descendingGroupsRes = applicationData?.applyBranchingQuery(allowedGroups, OtherGroupDescendingHierarchy!)
    const descendingGroups = descendingGroupsRes.getNodesOfType(GROUPER_TYPE).filter((n) => n.supportedType === allowedGroupSupportedType)

    debug &&
      console.log({
        querySubjects,
        applicationData,
        SubjectTypeToSubjectTypeGroupType,

        allowedGroupSupportedType,
        SubjectTypeGroups,
        ascendantGroups,
        allowedGroups,
        descendingGroups,
      })
    return descendingGroups
  }

  return query
}

type AsyncBranchingQuery = ({
  topLevelQuery,
  contextObjects,
  maxDepth,
}: GetBranchingQueryDefinitionResultsRequest) => ErrDataPromise<NodeEdgeMapper<ValidBusinessObject>>

/** async derivation of getIndirectGroupedTypeQuery which can be run agains database, returns a function bound to  querySubjects which will return allowed groups as an array of ValidBusinessObjects  from database */
export const getIndirectGroupedTypeQueryForDatabase = ({
  querySubjects,
  querySubjectType,
  allowedGroupSupportedType,
}: IndirectGroupedTypeToQueryInput): ((branchingQueryRunner: AsyncBranchingQuery) => ErrDataPromise<ValidBusinessObjectList>) => {
  const filteredQuerySubjects = querySubjects.filter((item) => item.type === querySubjectType)

  const SubjectTypeToSubjectTypeGroupType = new BranchingQueryDefinition({
    id: 'SubjectTypeToSubjectTypeGroupType',
    path: [
      {
        adjacencyType: AdjacentType.PARENT,
        objectTypeId: GROUPER_TYPE,
        edgeTypeId: getParentEdgeTypeId(querySubjectType, GROUPER_TYPE),
        nodeFilters: [
          { setOperation: LogicalOperatorType.AND, filters: [{ key: 'supportedType', predicate: ComparisonType.EQUALS, value: querySubjectType }] },
        ],
      },
    ],
    subQueries: [],
  })

  const SubjectTypeToAllSubjectTypeGroups = getNestedStructureBranchingQuery({
    groupObjectType: GROUPER_TYPE,
    containedObjectType: querySubjectType,
    traversal: Traversal.ASCENDING,
    includeSupportedTypeInstances: false,
  })

  const OtherGroupDescendingHierarchy = getNestedStructureBranchingQuery({
    groupObjectType: GROUPER_TYPE,
    containedObjectType: allowedGroupSupportedType,
    traversal: Traversal.DESCENDING,
    includeSupportedTypeInstances: false,
  })

  const allowsEdgeDirection = querySubjectType === USER_TYPE ? AdjacentType.CHILD : AdjacentType.PARENT

  const SubjectTypeGroupsToOtherGroupType = new BranchingQueryDefinition({
    id: 'SubjectTypeGroupsAllowsOtherGroupType',
    path: [
      {
        adjacencyType: allowsEdgeDirection,
        objectTypeId: GROUPER_TYPE,
        edgeTypeId: edgeTypeMatrix.Group.Group.group_allows_group.edgeTypeId,
        nodeFilters: [
          { setOperation: LogicalOperatorType.AND, filters: [{ key: 'supportedType', predicate: ComparisonType.EQUALS, value: allowedGroupSupportedType }] },
        ],
      },
    ],
    subQueries: [],
  })

  const query = async (branchingQueryRunner: AsyncBranchingQuery): ErrDataPromise<ValidBusinessObjectList> => {
    let res: ErrData<ValidBusinessObjectList> = { err: null, data: null }
    const SubjectTypeGroupsRes = await branchingQueryRunner({ topLevelQuery: SubjectTypeToSubjectTypeGroupType, contextObjects: filteredQuerySubjects })
    if (SubjectTypeGroupsRes.err) {
      res.err = SubjectTypeGroupsRes.err
    } else {
      const SubjectTypeGroups = SubjectTypeGroupsRes.data?.getNodesOfType(GROUPER_TYPE) ?? []
      if (SubjectTypeGroups?.length > 0) {
        const ascendantGroupsRes = await branchingQueryRunner({ contextObjects: SubjectTypeGroups, topLevelQuery: SubjectTypeToAllSubjectTypeGroups! })
        if (ascendantGroupsRes.err) {
          res.err = ascendantGroupsRes.err
        } else {
          const ascendantGroups = ascendantGroupsRes.data?.getNodesOfType(GROUPER_TYPE) ?? []
          if (ascendantGroups.length > 0) {
            const allowedGroupsRes = await branchingQueryRunner({ contextObjects: ascendantGroups, topLevelQuery: SubjectTypeGroupsToOtherGroupType })
            if (allowedGroupsRes.err) {
              res.err = allowedGroupsRes.err
            } else {
              const allowedGroups = (allowedGroupsRes.data?.getNodesOfType(GROUPER_TYPE) ?? []).filter(
                (n) => (n as ValidBusinessObject).supportedType === allowedGroupSupportedType,
              )
              if (allowedGroups.length > 0) {
                const descendingGroupsRes = await branchingQueryRunner({ contextObjects: allowedGroups, topLevelQuery: OtherGroupDescendingHierarchy! })
                if (descendingGroupsRes.err) {
                  res.err = descendingGroupsRes.err
                } else {
                  const descendingGroupsRes = await branchingQueryRunner({ contextObjects: allowedGroups, topLevelQuery: OtherGroupDescendingHierarchy! })
                  const descendingGroups = (descendingGroupsRes.data?.getNodesOfType(GROUPER_TYPE) ?? []).filter(
                    (n) => (n as ValidBusinessObject).supportedType === allowedGroupSupportedType,
                  )
                  res.data = descendingGroups as ValidBusinessObjectList

                  debug &&
                    console.log({
                      querySubjects,
                      SubjectTypeToSubjectTypeGroupType,

                      allowedGroupSupportedType,
                      SubjectTypeGroups,
                      ascendantGroups,
                      allowedGroups,
                      descendingGroups,
                    })
                }
              }
            }
          }
        }
      }
    }
    return res
  }

  return query
}
